// ══════════════════════════════════════════════════════════════════════════
// FILE 2 of 4: CardParts.jsx
// Small building blocks shared by both DiagnosticsCard and RoutineCard —
// header, the shared photo+stat top section, footer, and small row helpers.
// ══════════════════════════════════════════════════════════════════════════

import { useSelector } from 'react-redux'
import {
  FiHeart,
  FiGlobe,
  FiShield,
  FiCpu,
  FiFeather,
  FiSearch,
  FiTrendingUp,
} from 'react-icons/fi'
import QRCode from '../../assets/logos/qr.png'
import logo from '../../assets/logos/transparent-logo.png'

function useShareableTranslations() {
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}) => {
    let value = getTranslationText(keyPath)
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  return { translateText }
}

export function Header() {
  const { translateText } = useShareableTranslations()

  return (
    <div className='hs-header'>
      <div className='hs-logo-row'>
        <div className='hs-logo-mark'>
          <img src={logo} alt='Halisi Cosmetics' className='hs-logo-img' />
        </div>
      </div>

      <div className='hs-title-block'>
        <div className='hs-title'>
          <span className='hs-sparkle'>✦</span>{' '}
          {translateText('shareableFeature.header.titleLine1')}{' '}
          <span>{translateText('shareableFeature.header.titleLine2')}</span>{' '}
          {translateText('shareableFeature.header.titleLine3')}{' '}
          <span className='hs-sparkle'>✦</span>
        </div>
        <div className='hs-subtitle-row'>
          <div className='hs-subtitle-line' />
          <div className='hs-subtitle'>
            {translateText('shareableFeature.header.subtitle')}
          </div>
          <div className='hs-subtitle-line' />
        </div>
      </div>
    </div>
  )
}

// One row inside the photo-annotation column — either an icon+label pair
// (Natural State, Visible Hair Analysis) or a label+big-value pair (Wave Pattern).
export function AnnotationRow({ icon, label, value }) {
  return (
    <div className='hs-annotation-row'>
      <div className='hs-annotation-dot' />
      <div className='hs-annotation-line' />
      <div>
        {value ? (
          <>
            <div className='hs-annotation-label'>{label}</div>
            <div className='hs-annotation-value'>{value}</div>
          </>
        ) : (
          <div className='hs-annotation-label-icon'>
            <span className='hs-annotation-icon'>{icon}</span>
            {label}
          </div>
        )}
      </div>
    </div>
  )
}

// One column inside the "YOUR HAIR DNA" 3-column row (Diagnostics card only).
export function DnaCol({ icon, label, title, desc }) {
  return (
    <div className='hs-dna-col'>
      <div className='hs-dna-icon'>{icon}</div>
      <div className='hs-dna-label'>{label}</div>
      <div className='hs-dna-title'>{title}</div>
      <div className='hs-dna-desc'>{desc}</div>
    </div>
  )
}

// Shared top block: photo + annotations (left) and hair-type stat block
// (right). Identical on both the Diagnostics and Routine cards — this is
// the part of the reference design marked with both the blue "1" and the
// green "2".
export function TopSection({ hairTypeCode, hairTypeName, photoUrl, tagline }) {
  const { translateText } = useShareableTranslations()

  return (
    <div style={{ display: 'flex', gap: 14, height: 320 }}>
      <div className='hs-panel hs-photo-annotated' style={{ flex: 1.15 }}>
        <div className='hs-photo-inner'>
          {photoUrl && <img src={photoUrl} alt='Hair' />}
          <div className='hs-ai-badge'>
            <span className='hs-ai-badge-check'>✓</span>
            {translateText('shareableFeature.top.aiAnalysis')}
            <br />
            {translateText('shareableFeature.top.complete')}
          </div>
        </div>
        <div className='hs-annotations'>
          <AnnotationRow
            label={translateText('shareableFeature.top.wavePattern')}
            value={hairTypeCode}
          />
          <AnnotationRow
            icon={<FiFeather size={13} />}
            label={translateText('shareableFeature.top.naturalState')}
          />
          <AnnotationRow
            icon={<FiSearch size={13} />}
            label={translateText('shareableFeature.top.visibleAnalysis')}
          />
        </div>
      </div>

      <div className='hs-panel hs-stat-block' style={{ flex: 1 }}>
        <div className='hs-stat-code'>{hairTypeCode}</div>
        <div className='hs-stat-name'>{hairTypeName}</div>
        <div className='hs-natural-pill'>
          {translateText('shareableFeature.top.natural')}
        </div>
        <div className='hs-tag-row'>
          <span className='hs-tag'>
            ✦ {translateText('shareableFeature.top.shine')}
          </span>
          <span className='hs-tag'>
            <FiTrendingUp size={13} />{' '}
            {translateText('shareableFeature.top.volume')}
          </span>
        </div>
        <div className='hs-tagline'>"{tagline}"</div>
      </div>
    </div>
  )
}

export function CardFooter() {
  const { translateText } = useShareableTranslations()

  return (
    <>
      <div className='hs-footer'>
        <div className='hs-footer-left'>
          <div className='hs-footer-script'>
            <FiHeart size={22} />{' '}
            {translateText('shareableFeature.footer.wantYours')}
          </div>
          <div className='hs-footer-scan'>
            {translateText('shareableFeature.footer.scanQrLine1')}
            <br />
            <b>{translateText('shareableFeature.footer.free')}</b>{' '}
            {translateText('shareableFeature.footer.scanQrLine2')}
          </div>
        </div>
        <div className='hs-footer-pill'>
          <div className='hs-footer-pill-eyebrow'>
            {translateText('shareableFeature.footer.discoverYour')}
          </div>
          <div className='hs-footer-pill-title'>
            {translateText('shareableFeature.footer.hairDna')}
          </div>
          <div className='hs-footer-pill-sub'>
            {translateText('shareableFeature.footer.byHalisi')}
          </div>
          <div className='hs-footer-web'>
            <FiGlobe size={13} /> cosmetics.halisi.ai
          </div>
        </div>
        <img src={QRCode} alt='QR code' className='hs-footer-qr' />
      </div>

      <div className='hs-bottom-strip'>
        <div className='hs-bottom-item'>
          <FiShield size={13} />
          <div>
            <div className='hs-bottom-item-title'>
              {translateText('shareableFeature.footer.aiVerified')}
            </div>
            <div className='hs-bottom-item-sub'>
              {translateText('shareableFeature.footer.aiVerifiedSub')}
            </div>
          </div>
        </div>
        <div className='hs-bottom-item'>
          <FiCpu size={13} />{' '}
          {translateText('shareableFeature.footer.poweredBy')}{' '}
          <b>
            {translateText('shareableFeature.footer.artificialIntelligence')}
          </b>
        </div>
      </div>
    </>
  )
}
